import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useBusinessInfo, BusinessHours } from '@/hooks/useBusinessInfo';

const formatTime = (time: string) => {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hours = h % 12 || 12;
  return `${hours}:${m.toString().padStart(2, '0')} ${ampm}`;
};

const days: (keyof BusinessHours)[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const todayIndex = new Date().getDay(); // 0=Sun, 1=Mon...
// Map JS day index to our array index (Mon=0 in our array, Sun=6)
const todayArrayIndex = todayIndex === 0 ? 6 : todayIndex - 1;

export default function LocationSection() {
  const { t } = useTranslation();
  const { info, waLink, isOpenNow } = useBusinessInfo();

  return (
    <section id="location" className="bg-black py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-[#F57F25] text-xs font-bold tracking-widest uppercase mb-4">
            <div className="w-8 h-px bg-[#F57F25]"></div>
            {t('location_eyebrow')}
            <div className="w-8 h-px bg-[#F57F25]"></div>
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight mb-4">
            {t('location_headline')} <span className="text-[#F57F25]">{t('location_headline_accent')}</span>
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto">
            {t('location_sub')}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Contact Card */}
          <div className="w-full lg:w-[45%] bg-[#111] rounded-2xl p-8 border border-white/5 flex flex-col gap-6">
            {/* Business Name */}
            <div>
              <h3 className="text-[#F57F25] font-black text-2xl uppercase tracking-wide">{t('location_business_name')}</h3>
              <p className="text-white/50 text-sm mt-1">{t('location_business_sub')}</p>
            </div>

            {/* Address */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#F57F25]/10 rounded-full shrink-0 mt-0.5">
                <i className="ri-map-pin-line text-[#F57F25] text-lg"></i>
              </div>
              <div>
                <div className="text-white font-semibold text-sm">{t('location_address_label')}</div>
                <div className="text-white/60 text-sm mt-0.5">{info.address}</div>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#F57F25]/10 rounded-full shrink-0">
                <i className="ri-phone-line text-[#F57F25] text-lg"></i>
              </div>
              <div>
                <div className="text-white font-semibold text-sm">{t('location_phone_label')}</div>
                <a href={info.phone ? `tel:${info.phone.replace(/\\D/g, '')}` : '#'} className="text-[#F57F25] text-sm font-bold hover:underline cursor-pointer">
                  {info.phone}
                </a>
              </div>
            </div>

            {/* WhatsApp Button */}
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-base py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              {t('btn_order_whatsapp')}
            </a>

            {/* Business Hours */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-time-line text-[#F57F25]"></i>
                  </div>
                  <span className="text-white font-bold text-sm uppercase tracking-wide">{t('location_hours_label')}</span>
                </div>
                {/* Status Badge */}
                <div className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest ${isOpenNow ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                  {isOpenNow ? 'Open Now' : 'Closed'}
                </div>
              </div>
              <div className="space-y-2">
                {info.hours && days.map((dayKey, idx) => {
                  const h = info.hours![dayKey];
                  const isToday = idx === todayArrayIndex;
                  const isClosed = !h.isOpen;
                  return (
                    <div
                      key={dayKey}
                      className={`flex justify-between items-center py-1.5 px-3 rounded-lg text-sm ${
                        isToday ? 'bg-[#F57F25]/15 border border-[#F57F25]/30' : ''
                      }`}
                    >
                      <span className={`font-medium ${isToday ? 'text-[#F57F25]' : 'text-white/60'}`}>
                        {t(`day_${dayKey}`)}
                        {isToday && (
                          <span className="ml-2 text-xs bg-[#F57F25] text-black px-1.5 py-0.5 rounded-full">{t('location_today')}</span>
                        )}
                      </span>
                      <span className={`font-semibold ${isClosed ? 'text-red-400' : isToday ? 'text-white' : 'text-white/80'}`}>
                        {isClosed ? t('location_closed') : `${formatTime(h.openTime)} – ${formatTime(h.closeTime)}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Social Media */}
            <div>
              <div className="text-white font-bold text-sm uppercase tracking-wide mb-3">{t('location_follow_label')}</div>
              <div className="flex gap-3">
                {info.instagram && (
                  <a
                    href={info.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 flex items-center justify-center bg-[#F57F25]/10 hover:bg-[#F57F25] rounded-full transition-all duration-200 cursor-pointer group"
                    aria-label="Instagram"
                  >
                    <i className="ri-instagram-line text-[#F57F25] group-hover:text-black text-xl"></i>
                  </a>
                )}
                {info.facebook && (
                  <a
                    href={info.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 flex items-center justify-center bg-[#F57F25]/10 hover:bg-[#F57F25] rounded-full transition-all duration-200 cursor-pointer group"
                    aria-label="Facebook"
                  >
                    <i className="ri-facebook-line text-[#F57F25] group-hover:text-black text-xl"></i>
                  </a>
                )}
                {info.tiktok && (
                  <a
                    href={info.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 flex items-center justify-center bg-[#F57F25]/10 hover:bg-[#F57F25] rounded-full transition-all duration-200 cursor-pointer group"
                    aria-label="TikTok"
                  >
                    <i className="ri-tiktok-line text-[#F57F25] group-hover:text-black text-xl"></i>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="w-full lg:w-[55%] rounded-2xl overflow-hidden border border-white/5 min-h-[400px] lg:min-h-0">
            <iframe
              title="Bucara Burger Location"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(info.address)}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '450px' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="mt-12 bg-[#F57F25] rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-black font-black text-3xl md:text-4xl uppercase tracking-tight mb-3">
            {t('location_cta_title')}
          </h3>
          <p className="text-black/70 text-base mb-6">
            {t('location_cta_sub')}
          </p>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-black hover:bg-black/80 text-white font-bold text-base px-8 py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            {t('location_cta_btn')}
          </a>
        </div>
      </div>
    </section>
  );
}
